import { Spark } from '@sparkplug/lib';

import { TSparkStatus } from '@app/types/SparksTypes';

import { formatSparkInfo } from './sparks';

export interface EffectiveBundleCounts {
    participantCount: number;
    productCount: number;
    locationCount: number;
    retailerCount: number;
}

/**
 * Resolves bundle aggregate counts based on the spark's displayed status.
 *
 * When the spark is Active or Complete, returns active counts (participating retailers only).
 * Otherwise returns total counts (all retailers, representing potential reach).
 * Falls back gracefully for pre-existing bundles that haven't been re-aggregated yet.
 *
 * @returns resolved counts, or undefined if the spark has no bundle data
 */
export const getEffectiveBundleCounts = (
    bundle: Spark['bundle'],
    status: TSparkStatus,
): EffectiveBundleCounts | undefined => {
    const agg = bundle?.aggregates;
    if (!agg) return undefined;

    const useActive = status === 'Active' || status === 'Complete';

    return {
        participantCount:
            (useActive ? agg.activeParticipantCount : undefined) ?? agg.participantCount,
        productCount: (useActive ? agg.activeProductCount : undefined) ?? agg.productCount,
        locationCount: (useActive ? agg.activeLocationCount : undefined) ?? agg.locationCount,
        retailerCount: useActive
            ? (agg.acceptedCount ?? 0) + (agg.noneCount ?? 0)
            : agg.retailerCount,
    };
};

/**
 * Enriches a spark with status-aware effective bundle counts.
 * Resolves active vs total counts based on the spark's displayed status
 * so that downstream consumers don't need to repeat the logic.
 */
export const enrichSparkWithEffectiveCounts = <T extends Spark>(spark: T): T => {
    if (!spark.bundle) return spark;

    const { status } = formatSparkInfo(
        spark.startDate,
        spark.endDate,
        spark.bundleRequestState ?? spark.bundle?.requestState ?? spark.requestState,
        spark.recurringSchedule,
    );

    return {
        ...spark,
        effectiveBundleCounts: getEffectiveBundleCounts(spark.bundle, status),
    };
};
